import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Divider,
} from '@mui/material';
import api from '../api';
import dayjs from 'dayjs';

export default function ProjectWorkloadPanel({ projectId }) {
  const [monthlySummary, setMonthlySummary] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [monthlyDetails, setMonthlyDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  // xlsx 行18: 年間の担当者の時間数
  const [userSummary, setUserSummary] = useState([]);

  /* ===============================
   * 月別サマリー取得
   * =============================== */
  const loadSummary = async () => {
    try {
      const { data } = await api.get('/api/workloads/summary', {
        params: { projectId },
      });
      setMonthlySummary(data || []);
    } catch (err) {
      console.error('[workload summary error]', err);
    }
  };

  /* ===============================
   * 担当者別 年間サマリー
   * =============================== */
  const loadUserSummary = async () => {
    try {
      const { data } = await api.get('/api/workloads/summary-by-user', {
        params: { projectId },
      });
      setUserSummary(data || []);
    } catch (err) {
      console.error('[workload summary-by-user error]', err);
    }
  };

  /* ===============================
   * 月別詳細取得
   * =============================== */
  const loadDetails = async (month) => {
    try {
      setLoading(true);
      const { data } = await api.get('/api/workloads/by-month', {
        params: { projectId, month },
      });
      setMonthlyDetails(data || []);
    } catch (err) {
      console.error('[workload by-month error]', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) {
      loadSummary();
      loadUserSummary();
    }
  }, [projectId]);

  /* ===============================
   * CSVダウンロード
   * =============================== */
  const handleDownloadCsv = () => {
    if (!selectedMonth) return;
    const base = process.env.REACT_APP_API_BASE_URL || '';
    const url = `${base}/api/workloads/by-month/csv?projectId=${projectId}&month=${selectedMonth}`;
    window.open(url, '_blank');
  };

  /* ===============================
   * 月別合計（詳細）
   * =============================== */
  const detailTotal = monthlyDetails.reduce(
    (sum, r) => sum + (r.minutes || 0),
    0
  );

  return (
    <Box>
      {/* ===============================
       * 担当者別 年間サマリー (xlsx 行18)
       * =============================== */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          担当者別 年間工数 (直近12ヶ月)
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>担当者</TableCell>
              <TableCell align="right">年間合計 (分)</TableCell>
              <TableCell align="right">年間合計 (時間)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {userSummary.length > 0 ? (
              userSummary.map((u) => (
                <TableRow key={u.userId}>
                  <TableCell>{u.userName}</TableCell>
                  <TableCell align="right">{u.totalMinutes.toLocaleString()}</TableCell>
                  <TableCell align="right">{(u.totalMinutes / 60).toFixed(1)}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} align="center">担当者別データがありません</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* ===============================
       * 月別サマリー
       * =============================== */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          月別工数サマリー
        </Typography>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>月</TableCell>
              <TableCell align="right">合計時間（分）</TableCell>
              <TableCell align="center">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {monthlySummary.length > 0 ? (
              monthlySummary.map((m) => (
                <TableRow key={m.month} hover>
                  <TableCell>{m.month}</TableCell>
                  <TableCell align="right">
                    {m.totalMinutes.toLocaleString()}
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      variant={
                        selectedMonth === m.month ? 'contained' : 'outlined'
                      }
                      onClick={() => {
                        setSelectedMonth(m.month);
                        loadDetails(m.month);
                      }}
                    >
                      詳細
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  工数データがありません
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* ===============================
       * 月別詳細
       * =============================== */}
      {selectedMonth && (
        <Paper sx={{ p: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1,
            }}
          >
            <Typography variant="h6">
              {selectedMonth} の工数詳細
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={handleDownloadCsv}
            >
              CSVダウンロード
            </Button>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>日付</TableCell>
                <TableCell>担当者</TableCell>
                <TableCell>作業内容</TableCell>
                <TableCell align="right">時間（分）</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    読み込み中...
                  </TableCell>
                </TableRow>
              ) : monthlyDetails.length > 0 ? (
                monthlyDetails.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      {r.workDate
                        ? dayjs(r.workDate).format('YYYY/MM/DD')
                        : '-'}
                    </TableCell>
                    <TableCell>{r.userName || '-'}</TableCell>
                    <TableCell>{r.memo || '-'}</TableCell>
                    <TableCell align="right">
                      {(r.minutes || 0).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    データがありません
                  </TableCell>
                </TableRow>
              )}

              {/* 合計 */}
              {monthlyDetails.length > 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>
                    合計
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {detailTotal.toLocaleString()} 分
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
}
